import { jsPDF } from 'jspdf';

import {
  CuripodDecendant,
  CuripodElement,
  CuripodMathInlineElement,
  CuripodText,
  isCuripodMathInlineElement,
  isCuripodText,
} from '../../components/SlideComponents/slateUtil';
import { renderMathToImage } from './renderMath';
import { MathImageToken, ParagraphToken } from './renderParagraphLine';
import renderParagraphLine from './renderParagraphLine';
import { Alignment } from './renderTextElement';
import { setPdfTextStyling } from './renderUtils';

type RenderParagraphProps = {
  pdf: jsPDF;
  paraElement: CuripodElement;
  x: number;
  y: number;
  width: number;
  scale: number;
};

const renderParagraph = async ({
  pdf,
  x,
  y,
  width,
  paraElement,
  scale,
}: RenderParagraphProps) => {
  const alignment = (paraElement.align as Alignment) || 'left';
  const currentX = x;
  let currentY = y;
  if (!paraElement.children || !Array.isArray(paraElement.children))
    return { updatedY: currentY };

  const paraWords: ParagraphToken[] = [];

  const handleWordSplit = (word: CuripodText): CuripodText[] => {
    setPdfTextStyling({
      pdf,
      color: word.color || '#000',
      bold: !!word.bold,
      italic: !!word.italic,
    });
    const textWidth = pdf.getTextDimensions(word.text).w;
    if (textWidth > width) {
      let firstPartText = '';
      for (const char of word.text) {
        const subTextWidth = firstPartText
          ? pdf.getTextDimensions(firstPartText + char).w
          : 0;
        if (subTextWidth > width) {
          break;
        } else {
          firstPartText = firstPartText + char;
        }
      }

      const fittingText = firstPartText;
      const nonFittingText = word.text.substring(firstPartText.length);

      return [
        ...handleWordSplit({ ...word, text: fittingText }),
        ...handleWordSplit({ ...word, text: nonFittingText }),
      ];
    } else {
      return [word];
    }
  };

  const pdfFontSize = pdf.getFontSize();

  for (const eachParaChild of paraElement.children as CuripodDecendant[]) {
    if (isCuripodMathInlineElement(eachParaChild)) {
      const mathEl = eachParaChild as CuripodMathInlineElement;
      const color = (mathEl.children?.[0] as CuripodText | undefined)?.color || undefined;
      const mathImage = await renderMathToImage(mathEl.value, {
        fontSize: pdfFontSize,
        color,
      });
      if (mathImage) {
        const token: MathImageToken = {
          tokenType: 'math-image',
          dataUrl: mathImage.dataUrl,
          width: mathImage.width,
          height: mathImage.height,
          color,
        };
        paraWords.push(token);
      }
      continue;
    }

    if (!isCuripodText(eachParaChild)) continue;
    if (!eachParaChild.text) continue;

    const words = eachParaChild.text.split(/(\s+)/).flatMap(word => {
      return word.split(/(\n)/).filter(Boolean);
    });
    for (const eachWord of words) {
      const sizeHandledWords = handleWordSplit({
        ...eachParaChild,
        text: eachWord,
      });
      paraWords.push(...sizeHandledWords);
    }
  }

  const allLines: ParagraphToken[][] = [];
  let currentLine: ParagraphToken[] = [];
  let currentLineWidth = 0;

  for (const token of paraWords) {
    if (token.tokenType === 'math-image') {
      const tokenWidth = token.width;
      if (currentLineWidth + tokenWidth > width && currentLine.length > 0) {
        allLines.push(currentLine);
        currentLine = [token];
        currentLineWidth = tokenWidth;
      } else {
        currentLine.push(token);
        currentLineWidth += tokenWidth;
      }
    } else {
      const textWidth = pdf.getTextDimensions(token.text).w;
      if (token.text === '\n') {
        allLines.push(currentLine);
        currentLine = [];
        currentLineWidth = 0;
      } else if (currentLineWidth + textWidth > width) {
        allLines.push(currentLine);
        currentLine = [token];
        currentLineWidth = textWidth;
      } else {
        currentLine.push(token);
        currentLineWidth += textWidth;
      }
    }
  }
  if (currentLine.length) allLines.push(currentLine);

  for (const eachLine of allLines) {
    const { updatedY } = await renderParagraphLine({
      pdf,
      x: currentX,
      y: currentY,
      width,
      alignment,
      paraLine: eachLine,
      scale,
    });
    currentY = updatedY;
  }

  return { updatedY: currentY };
};

export default renderParagraph;
